import { Link } from 'react-router';
import { DataTable, Notice, State } from '@p1plug/ui';
import { C, DocPage, H2, P } from '../doc';

type Stage = 'planned' | 'operated';

const SCENARIOS: { name: string; simulates: string; receive: string; stage: Stage }[] = [
  {
    name: 'Synthetic device',
    simulates: 'A virtual P1Plug with a serial, running the real device protocol',
    receive: 'Everything a physical device produces',
    stage: 'operated',
  },
  {
    name: 'Test order',
    simulates: 'An order in the sandbox; no parcel leaves a warehouse',
    receive: 'order.status_changed',
    stage: 'operated',
  },
  {
    name: 'Shipping',
    simulates: 'Packed, shipped and delivered on your command',
    receive: 'order.status_changed with tracking_code',
    stage: 'operated',
  },
  {
    name: 'Activation',
    simulates: 'The customer plugging the device in',
    receive: 'installation.activated',
    stage: 'operated',
  },
  {
    name: 'P1 telegrams',
    simulates: 'Readings with a household profile: base load, peaks, solar feed-in, one or three phases',
    receive: 'telemetry.electricity',
    stage: 'operated',
  },
  {
    name: 'Offline and return',
    simulates: 'A device losing its connection and delivering its buffer afterwards',
    receive: 'A pause, then telemetry with backfill: true',
    stage: 'operated',
  },
  {
    name: 'Device replacement',
    simulates: 'An RMA: a new device on the same meter point',
    receive: 'installation.replaced',
    stage: 'operated',
  },
  {
    name: 'Firmware and device states',
    simulates: 'A device mid-update, or reporting a fault',
    receive: 'Device status changes',
    stage: 'planned',
  },
  {
    name: 'Never activated',
    simulates: 'A delivered device that stays silent',
    receive: 'Nothing: test your own reminders',
    stage: 'planned',
  },
  {
    name: 'Failure injection',
    simulates: 'API errors, slow responses and rate limits on request',
    receive: 'The error codes on the Errors page',
    stage: 'planned',
  },
  {
    name: 'Webhook replay',
    simulates: 'Re-sending any past delivery to your endpoint',
    receive: 'The same batch, same event_ids',
    stage: 'planned',
  },
];

export function Sandbox() {
  return (
    <DocPage
      title="Sandbox"
      lead="The sandbox lets you run the whole P1Plug lifecycle, including the parts that go wrong, without a device, a parcel or a customer."
    >
      <Notice tone="info" title="Where the sandbox stands">
        The scenarios marked “Operated by P1Plug” exist today on virtual devices that speak the real device protocol;
        P1Plug runs them for you in a test environment. Self-service controls, a console on this site and the remaining
        scenarios are being built. The API you integrate against does not change.
      </Notice>

      <H2>What you can test</H2>
      <DataTable
        rowKey={(s) => s.name}
        columns={[
          { key: 'name', label: 'Scenario' },
          { key: 'simulates', label: 'What it simulates' },
          { key: 'receive', label: 'What your integration receives' },
          {
            key: 'stage',
            label: 'Availability',
            render: (s) =>
              s.stage === 'operated' ? (
                <State tone="info" label="Operated by P1Plug" />
              ) : (
                <State tone="unknown" label="Planned" />
              ),
          },
        ]}
        rows={SCENARIOS}
      />

      <H2>How it will work</H2>
      <ol className="p1x-prose">
        <li>
          <strong>Create a sandbox application</strong> under{' '}
          <Link to="/account/credentials">Applications and credentials</Link>. You get sandbox credentials; they cannot
          reach production.
        </li>
        <li>
          <strong>Create an order</strong> with the normal API. In the sandbox it is linked to a synthetic device
          immediately.
        </li>
        <li>
          <strong>Drive the lifecycle</strong> from the sandbox console or its API: ship, deliver, activate, take
          offline, bring back, replace.
        </li>
        <li>
          <strong>Watch your integration</strong>: every request you made and every webhook P1Plug sent, with status,
          latency and payload, under <Link to="/account/usage">API usage</Link>.
        </li>
        <li>
          <strong>Switch to production</strong> by changing the base URL and the credential. Nothing else.
        </li>
      </ol>

      <H2>What the sandbox will never do</H2>
      <ul className="p1x-prose">
        <li>Ship hardware or create a real installation.</li>
        <li>
          Mix with production: separate credentials, identifiers and event logs. See{' '}
          <Link to="/docs/environments">Environments</Link>.
        </li>
        <li>
          Behave differently from production in the contract. A scenario changes what happens, never the shape of{' '}
          <C>/api/v1</C>.
        </li>
      </ul>
      <P>To get test access today, ask your P1Plug contact for a test organisation and credentials.</P>
    </DocPage>
  );
}
